import { IAugmentedJQuery, IComponentOptions, IPromise, IScope, ITimeoutService } from "angular";
import { searchItemsToAssignees } from "@gtmhub/assignees/utils/search-utils";
import { GtmhubController } from "@gtmhub/core";
import dayjs from "@gtmhub/dayjs";
import { IIndicator } from "@gtmhub/error-handling";
import { ISelectedGoal, ISelectedMetric } from "@gtmhub/goals/components/single-goal-selector/single-goal-selector.component";
import { IGoal } from "@gtmhub/goals/models";
import { GoalModalsService } from "@gtmhub/goals/services/goal-modals.service";
import { SearchService } from "@gtmhub/search/services/search.service";
import { handleOutsideMouseEvent } from "@gtmhub/shared/utils/close-on-mousedown";
import { getCurrentUserId } from "@gtmhub/users";
import { buildUsersSearchRequest } from "@gtmhub/users/utils/search-utils";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { ICollection } from "@webapp/core/core.models";
import { ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search } from "@webapp/search/models/search.models";
import { NewKanbanTask } from "@webapp/tasks/models/tasks.models";
import { isTask } from "@webapp/tasks/utils/tasks.utils";
import { TaskService } from "../../services/task.service";
import { TaskEvents } from "../../task.events";

interface CreateKanbanTaskFormBindings {
  close(): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CreateKanbanTaskFormCtrl extends CreateKanbanTaskFormBindings {}
class CreateKanbanTaskFormCtrl extends GtmhubController {
  public isCalendarOpen?: boolean;
  public task: NewKanbanTask;
  public selectedParent: IGoal | Metric;
  public customFields: ICollection<ICustomField>;
  public indicators: {
    loading?: IIndicator;
    saving?: IIndicator;
    selectingAssignee?: boolean;
  };
  private taskEvents: TaskEvents;

  public static $inject = ["$scope", "$element", "$timeout", "GoalModalsService", "TaskService", "SearchService"];

  constructor(
    private $scope: IScope,
    private $element: IAugmentedJQuery,
    private $timeout: ITimeoutService,
    private goalModalsService: GoalModalsService,
    private tasksService: TaskService,
    private searchService: SearchService
  ) {
    super();

    this.taskEvents = new TaskEvents(this.$scope);
    this.indicators = {};
    this.selectedParent = {
      sessionId: "",
    };

    this.init();
  }

  private init(): void {
    this.task = {
      ownerId: getCurrentUserId(),
      status: "todo",
    };

    this.bindOutsideClickToCloseForm();
    this.setAutofocus();
  }

  public setDueDate(date: string): void {
    this.task.dueDate = dayjs(date).toISOString();
  }

  public removeDueDate(): void {
    delete this.task.dueDate;
  }

  public openGoalSelector(): void {
    const invokedById = isTask(this.task) ? this.task.id : undefined;
    this.goalModalsService.openGoalSelectModal({ unselectableId: this.task.parentId, invokedById, invokedBy: "TaskKanban" }).then((selection) => {
      if (selection.type === "goal") {
        this.goalSelected(selection.goal);
      } else if (selection.type === "metric") {
        this.metricSelected(selection.metric);
      }
    });
  }

  private goalSelected(goal: ISelectedGoal): void {
    this.task.parentId = goal.id;
    this.task.parentType = "goal";
    this.selectedParent = {
      name: goal.name,
      sessionId: goal.sessionId,
    };
  }

  private metricSelected(metric: ISelectedMetric): void {
    this.task.parentId = metric.id;
    this.task.parentType = "metric";
    this.selectedParent = {
      name: metric.name,
      sessionId: metric.sessionId,
    };
  }

  private createTask(): void {
    this.indicators.saving = { progress: true };
    this.tasksService.create(this.task).then(
      (createdTask) => {
        delete this.indicators.saving;
        let sessionId = null;
        if (this.selectedParent && this.selectedParent.sessionId) {
          sessionId = this.selectedParent.sessionId;
        }

        this.taskEvents.broadcastTaskCreated({ task: createdTask, sessionId });
      },
      (error) => (this.indicators.saving = { error })
    );
  }

  public keyup(event: KeyboardEvent): void {
    if (event.key === "Enter" && this.task.name) {
      this.createTask();
    }
  }

  private bindOutsideClickToCloseForm = (): void => {
    const unsubscribe = handleOutsideMouseEvent(
      "mousedown",
      () => {
        if (this.task.name) {
          this.createTask();
        } else {
          this.close();
          this.$scope.$evalAsync();
        }

        unsubscribe();
      },
      this.$element,
      "div.ant-picker-dropdown.ui-date-picker-dropdown",
      ".modal"
    );
    this.onDestroy(unsubscribe);
  };

  public getAssignees(searchTerm: string): IPromise<Assignee[]> {
    const searchBody = {
      searchTerm,
      operator: searchTerm ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
      searchRequests: [buildUsersSearchRequest(searchTerm)],
    };

    return this.searchService.getSomeSearchData<Search<"users">>(searchBody).then(
      (response) => searchItemsToAssignees(response.items),
      () => []
    );
  }

  public selectNewAssignee(assignee: Assignee): void {
    this.task.ownerId = assignee.id;
    this.indicators.selectingAssignee = true;

    this.$timeout(() => {
      (document.getElementById("inputFieldSelectNewAssignee") as HTMLInputElement).value = "";

      this.$timeout(() => {
        document.getElementById("inputFieldSelectNewAssignee").blur();
        this.indicators.selectingAssignee = false;
      }, 0);
    }, 0);
  }

  public typeaheadBlur(): void {
    this.$timeout(() => {
      (document.getElementById("inputFieldSelectNewAssignee") as HTMLInputElement).value = "";
    }, 0);
  }

  private setAutofocus(): void {
    this.$timeout(() => {
      this.$scope.$broadcast("focusTextInput");
    }, 0);
  }
}

export const CreateKanbanTaskFormComponent: IComponentOptions = {
  controller: CreateKanbanTaskFormCtrl,
  template: require("./create-kanban-task-form.component.html"),
  bindings: {
    close: "&",
  },
};
